import { FC, ReactNode, useMemo, useState } from 'react';
import { Avatar, Button, Checkbox, Col, Divider, Modal, Row, Tabs } from 'antd';
import { CalendarDays, ListTree } from 'lucide-react';
import {
    TbBuilding,
    TbDatabase,
    TbFileText,
    TbLayoutGrid,
    TbPointerBolt,
    TbRefresh,
    TbSettings2,
    TbUsers,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDataSources } from '@/hooks/useDataSources';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMaps } from '@/hooks/useMaps';
import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import { LearnMoreFooter, PlanTag, preferenceModalBodyStyle, SmallTag, SmallTooltip } from '@/shared';
import { DragDropContext, Draggable, DragHandle, Droppable, DropResult } from '@/shared/components/drag-and-drop';
import AddDataSourceModal from '@/shared/components/modals/ResourceCreationModals/AddDataSourceModal';
import RequiredAddOnSmallTag from '@/shared/components/tags/RequiredAddOnSmallTag';
import { normalizeFeatureAddOnKey } from '@/shared/entitlements';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType, DataSourceOption } from '@/types';

import {
    applyCommandPaletteTabPreferences,
    COMMAND_PALETTE_ALL_TAB_KEY,
    commandPalettePlatformTabs,
    CommandPalettePreferences,
    CommandPaletteTabModel,
    CommandPaletteTabSection,
    CommandPaletteTabSectionKey,
    getCommandPaletteSectionPreferences,
    isCommandPaletteSearchableProviderGroup,
    orderCommandPaletteTabItems,
    providerGroupForConnectedAccountType,
} from './preferences';

interface CommandPaletteTabsProps {
    activeKey: string;
    connectedAccounts: ConnectedAccount[];
    countByTab: Record<string, number>;
    onChange: (key: string) => void;
}

const sectionPath = (sectionKey: CommandPaletteTabSectionKey, preferenceKey: 'hidden' | 'order' | 'visible') =>
    `commandPalette.tabs.${sectionKey}.${preferenceKey}`;

const draggableIdFor = (sectionKey: CommandPaletteTabSectionKey, itemKey: string) =>
    `command-palette:${sectionKey}:${itemKey}`;

const dataSourceSearchDescriptionByType: Partial<Record<ConnectedAccountType, string>> = {
    [ConnectedAccountType.AIRTABLE]: 'Search Airtable bases, tables, and records from the Command Palette.',
    [ConnectedAccountType.ASANA]: 'Search Asana projects, tasks, and collaborators from the Command Palette.',
    [ConnectedAccountType.ATTIO]:
        'Search Attio companies, people, lists, and relationship records from the Command Palette.',
    [ConnectedAccountType.BOX]: 'Search Box files, folders, and shared workspace documents from the Command Palette.',
    [ConnectedAccountType.CLICKUP]:
        'Search ClickUp spaces, lists, tasks, and project records from the Command Palette.',
    [ConnectedAccountType.CLOSE]:
        'Search Close leads, contacts, opportunities, and relationship records from the Command Palette.',
    [ConnectedAccountType.CODA]: 'Search Coda docs, tables, and pages from the Command Palette.',
    [ConnectedAccountType.CONFLUENCE]:
        'Search Confluence spaces, pages, and knowledge-base content from the Command Palette.',
    [ConnectedAccountType.DOCUSIGN]:
        'Search DocuSign envelopes, agreements, and signature workflows from the Command Palette.',
    [ConnectedAccountType.DROPBOX]:
        'Search Dropbox files, folders, and shared workspace documents from the Command Palette.',
    [ConnectedAccountType.GMAIL]:
        'Search Gmail messages, contacts, and calendar-linked context from the Command Palette.',
    [ConnectedAccountType.GOOGLE_DRIVE]: 'Search Google Drive contents from the Command Palette.',
    [ConnectedAccountType.GOOGLE_SHEETS]: 'Search Google Sheets workbooks, tabs, and records from the Command Palette.',
    [ConnectedAccountType.HUBSPOT]:
        'Search HubSpot companies, contacts, deals, and relationship records from the Command Palette.',
    [ConnectedAccountType.MICROSOFT_365_OUTLOOK]:
        'Search Outlook mail, contacts, and calendar-linked context from the Command Palette.',
    [ConnectedAccountType.MIRO]: 'Search Miro boards and visual collaboration assets from the Command Palette.',
    [ConnectedAccountType.MONDAY]: 'Search monday.com boards, groups, and items from the Command Palette.',
    [ConnectedAccountType.NOTION]: 'Search Notion pages, databases, and workspace knowledge from the Command Palette.',
    [ConnectedAccountType.PIPEDRIVE]:
        'Search Pipedrive people, organizations, deals, and relationship records from the Command Palette.',
    [ConnectedAccountType.SALESFORCE]:
        'Search Salesforce accounts, contacts, leads, and opportunities from the Command Palette.',
    [ConnectedAccountType.SLACK]:
        'Search Slack channels, messages, and shared collaboration context from the Command Palette.',
    [ConnectedAccountType.SMARTSHEET]: 'Search Smartsheet sheets, rows, and project data from the Command Palette.',
    // [ConnectedAccountType.WEALTHBOX]:
    //     'Search Wealthbox contacts, households, and relationship records from the Command Palette.',
    [ConnectedAccountType.WRIKE]: 'Search Wrike folders, projects, tasks, and collaborators from the Command Palette.',
};

const dataSourceSearchDescriptionFor = (type?: ConnectedAccountType | string, fallbackLabel = 'this data source') =>
    dataSourceSearchDescriptionByType[type as ConnectedAccountType] ??
    `Search ${fallbackLabel} content from the Command Palette.`;

const CommandPaletteTabs: FC<CommandPaletteTabsProps> = ({
    activeKey,
    connectedAccounts,
    countByTab,
    onChange,
}): JSX.Element => {
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [dataSourceModalOpen, setDataSourceModalOpen] = useState(false);
    const [dataSourceModalView, setDataSourceModalView] = useState<'add' | 'manage'>('add');
    const { preferences, setPreference } = useUserPreferences();
    const commandPalettePreferences = preferences?.commandPalette as CommandPalettePreferences | undefined;
    const { token } = useAntDesignTheme();
    const { integrationLabelToIconMap } = useMaps();
    const { dataSources } = useDataSources();
    const { hasFeatureAddOn, hasMinimumPlan } = useEntitlements();
    const { getUsageState } = usePlanUsageLimits();
    const dataSourceUsage = getUsageState('dataSources');
    const preferencesModalZIndex = token.zIndexPopupBase + 2000;
    const dataSourceModalZIndex = preferencesModalZIndex + 1000;

    const dataSourceTabs = useMemo<CommandPaletteTabModel[]>(
        () =>
            connectedAccounts
                .map((account) => {
                    const providerGroup = providerGroupForConnectedAccountType(account.type);

                    return {
                        connectedAccountId: account.id,
                        description: dataSourceSearchDescriptionFor(account.type, account.nickname || account.type),
                        key: `dataSource:${account.id}`,
                        label: account.nickname || account.type || 'Data Source',
                        providerGroup,
                        section: 'dataSources' as const,
                        sourceType: account.type,
                    };
                })
                .filter((tab) => isCommandPaletteSearchableProviderGroup(tab.providerGroup)),
        [connectedAccounts],
    );

    const sections = useMemo<CommandPaletteTabSection[]>(
        () => [
            {
                key: 'platform',
                title: 'Platform',
                lockedItemKeys: [COMMAND_PALETTE_ALL_TAB_KEY],
                items: commandPalettePlatformTabs,
            },
            {
                key: 'dataSources',
                title: 'Data Sources',
                items: dataSourceTabs,
            },
        ],
        [dataSourceTabs],
    );

    const visibleTabs = useMemo(
        () =>
            sections.flatMap((section) =>
                applyCommandPaletteTabPreferences(
                    section.items,
                    getCommandPaletteSectionPreferences(commandPalettePreferences, section.key),
                ),
            ),
        [commandPalettePreferences, sections],
    );

    const connectedAccountTypes = useMemo(
        () => new Set(connectedAccounts.map((account) => account.type).filter(Boolean)),
        [connectedAccounts],
    );

    const availableDataSourceOptions = useMemo<DataSourceOption[]>(() => {
        const byKey = new Map<string, DataSourceOption>();

        dataSources.forEach((section) => {
            section.options.forEach((option) => {
                const providerGroup = providerGroupForConnectedAccountType(option.key);

                if (
                    option.active === false ||
                    byKey.has(option.key) ||
                    !isCommandPaletteSearchableProviderGroup(providerGroup)
                ) {
                    return;
                }

                byKey.set(option.key, option);
            });
        });

        return Array.from(byKey.values());
    }, [dataSources]);

    const unconnectedDataSourceOptions = useMemo(
        () =>
            availableDataSourceOptions.filter(
                (option) => !connectedAccountTypes.has(option.key as ConnectedAccountType),
            ),
        [availableDataSourceOptions, connectedAccountTypes],
    );

    const tabIconByKey = (tab: CommandPaletteTabModel, size = 16, withTrailingMargin = true): ReactNode => {
        if (tab.section === 'dataSources') {
            const src = tab.sourceType ? integrationLabelToIconMap[tab.sourceType as ConnectedAccountType] : undefined;

            return (
                <Avatar
                    size={size}
                    shape='square'
                    style={{
                        backgroundColor: token.colorBgContainer,
                        border: `1px solid ${token.colorBorder}`,
                        marginLeft: withTrailingMargin ? 0 : '0.125rem',
                        marginRight: withTrailingMargin ? '0.25rem' : 0,
                        verticalAlign: '-0.175rem',
                    }}
                    icon={
                        src ? (
                            <img alt={tab.label} src={src} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
                        ) : (
                            <TbDatabase size='0.75rem' style={{ color: token.colorTextTertiary }} />
                        )
                    }
                />
            );
        }

        const iconProps = {
            size: size <= 16 ? '0.7rem' : '0.8rem',
            style: { color: token.colorTextTertiary, verticalAlign: '-0.1rem' },
        };

        const map = {
            [COMMAND_PALETTE_ALL_TAB_KEY]: <ListTree {...iconProps} />,
            workspace: <TbLayoutGrid {...iconProps} />,
            actions: <TbPointerBolt {...iconProps} />,
            companies: <TbBuilding {...iconProps} />,
            documents: <TbFileText {...iconProps} />,
            people: <TbUsers {...iconProps} />,
        } as Record<string, ReactNode>;

        return (
            <Avatar
                size={size}
                shape='square'
                style={{
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginLeft: withTrailingMargin ? 0 : '0.125rem',
                    marginRight: withTrailingMargin ? '0.25rem' : 0,
                    verticalAlign: '-0.175rem',
                }}
                icon={map[tab.key] ?? <TbDatabase {...iconProps} />}
            />
        );
    };

    const resetSection = (section: CommandPaletteTabSection) => {
        setPreference(`commandPalette.tabs.${section.key}`, {
            hidden: [],
            order: section.items.map((item) => item.key),
            visible: [],
        });
    };

    const resetAll = () => {
        setPreference('commandPalette.tabs', {
            platform: {
                hidden: [],
                order: commandPalettePlatformTabs.map((item) => item.key),
                visible: [],
            },
            dataSources: {
                hidden: [],
                order: dataSourceTabs.map((item) => item.key),
                visible: [],
            },
        });
    };

    const handleDragEnd = (section: CommandPaletteTabSection, result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const sectionPreferences = getCommandPaletteSectionPreferences(commandPalettePreferences, section.key);
        const orderedItems = orderCommandPaletteTabItems(section.items, sectionPreferences);
        const [moved] = orderedItems.splice(result.source.index, 1);

        orderedItems.splice(result.destination.index, 0, moved);
        setPreference(
            sectionPath(section.key, 'order'),
            orderedItems.map((item) => item.key),
        );
    };

    const handleVisibilityToggle = (section: CommandPaletteTabSection, item: CommandPaletteTabModel) => {
        if (section.lockedItemKeys?.includes(item.key) || item.locked) {
            return;
        }

        const sectionPreferences = getCommandPaletteSectionPreferences(commandPalettePreferences, section.key);
        const hidden = new Set(sectionPreferences.hidden ?? []);
        const visible = new Set(sectionPreferences.visible ?? []);
        const checked = item.locked || (!hidden.has(item.key) && (!item.defaultHidden || visible.has(item.key)));

        if (!checked) {
            hidden.delete(item.key);
            if (item.defaultHidden) {
                visible.add(item.key);
            }
        } else {
            hidden.add(item.key);
            visible.delete(item.key);
        }

        setPreference(sectionPath(section.key, 'hidden'), Array.from(hidden));
        setPreference(sectionPath(section.key, 'visible'), Array.from(visible));

        if (hidden.has(activeKey)) {
            onChange(COMMAND_PALETTE_ALL_TAB_KEY);
        }
    };

    const renderManagerItem = (section: CommandPaletteTabSection, item: CommandPaletteTabModel, index: number) => {
        const sectionPreferences = getCommandPaletteSectionPreferences(commandPalettePreferences, section.key);
        const hidden = new Set(sectionPreferences.hidden ?? []);
        const visible = new Set(sectionPreferences.visible ?? []);
        const locked = section.lockedItemKeys?.includes(item.key) || item.locked;
        const checked = locked || (!hidden.has(item.key) && (!item.defaultHidden || visible.has(item.key)));

        return (
            <Draggable draggableId={draggableIdFor(section.key, item.key)} index={index} key={item.key}>
                {(provided, snapshot) => (
                    <Row
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        align='middle'
                        gutter={[8, 0]}
                        style={{
                            backgroundColor: snapshot.isDragging ? token.colorBgElevated : undefined,
                            borderRadius: token.borderRadiusSM,
                            margin: snapshot.isDragging ? 0 : '0.25rem 0',
                            padding: '0 0.25rem',
                            ...provided.draggableProps.style,
                        }}
                        wrap={false}
                    >
                        <Col flex='none'>
                            <DragHandle
                                dragHandleProps={provided.dragHandleProps}
                                isDragging={snapshot.isDragging}
                                label={`Reorder ${item.label}`}
                            />
                        </Col>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <div
                                style={{
                                    alignItems: 'center',
                                    color: token.colorTextSecondary,
                                    display: 'flex',
                                    gap: '0.25rem',
                                    minWidth: 0,
                                }}
                            >
                                <SmallTooltip title={locked ? 'Required' : 'Show in Command Palette'} placement='left'>
                                    <Checkbox
                                        checked={checked}
                                        disabled={locked}
                                        onChange={() => handleVisibilityToggle(section, item)}
                                    />
                                </SmallTooltip>
                                {tabIconByKey(item, item.section === 'dataSources' ? 20 : 16, false)}
                                <span
                                    style={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.label}
                                </span>
                                {item.key === 'people' && (
                                    <SmallTag
                                        icon={
                                            <CalendarDays
                                                size='0.7rem'
                                                style={{
                                                    color: token.colorTextTertiary,
                                                    marginRight: '0.25rem',
                                                    verticalAlign: '-0.1rem',
                                                }}
                                            />
                                        }
                                        message='Requires Email & Calendar Data Source'
                                        padding='0.075rem 0.25rem'
                                        styles={{
                                            color: token.colorTextTertiary,
                                            flex: '0 0 auto',
                                            marginLeft: '0.25rem',
                                        }}
                                    />
                                )}
                            </div>
                            <small
                                style={{
                                    color: token.colorTextTertiary,
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.description}
                            </small>
                        </Col>
                    </Row>
                )}
            </Draggable>
        );
    };

    const renderDataSourceConnectItem = (option: DataSourceOption) => {
        const src = option.src ?? integrationLabelToIconMap[option.key as ConnectedAccountType];
        const label = option.label ?? option.key;
        const missingRequiredPlan = Boolean(option.minimumPlanForUse && !hasMinimumPlan(option.minimumPlanForUse));
        const missingRequiredAddOn = option.requiredAddOnsForUse?.some((requiredAddOn) => {
            const featureAddOn = normalizeFeatureAddOnKey(requiredAddOn);

            return !featureAddOn || !hasFeatureAddOn(featureAddOn);
        });

        return (
            <Row
                align='middle'
                gutter={[8, 0]}
                key={`connect-${option.key}`}
                style={{
                    borderRadius: token.borderRadiusSM,
                    margin: '0.25rem 0',
                    padding: '0.25rem',
                }}
                wrap={false}
            >
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <div
                        style={{
                            alignItems: 'center',
                            color: token.colorTextSecondary,
                            display: 'flex',
                            gap: '0.375rem',
                            minWidth: 0,
                        }}
                    >
                        <Avatar
                            size={20}
                            shape='square'
                            style={{
                                backgroundColor: token.colorBgContainer,
                                border: `1px solid ${token.colorBorder}`,
                                flex: '0 0 auto',
                                verticalAlign: '-0.175rem',
                            }}
                            icon={
                                src ? (
                                    <img
                                        alt={String(label)}
                                        src={src}
                                        style={{ ...imgLogoStyling, ...(option.avatarStyling ?? {}) }}
                                    />
                                ) : (
                                    <TbDatabase size='0.75rem' style={{ color: token.colorTextTertiary }} />
                                )
                            }
                        />
                        <span
                            style={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {label}
                        </span>
                        {missingRequiredPlan && option.minimumPlanForUse && (
                            <PlanTag plan={option.minimumPlanForUse} style={{ flex: '0 0 auto' }} />
                        )}
                        {missingRequiredAddOn ? <RequiredAddOnSmallTag i={option} /> : null}
                    </div>
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {dataSourceSearchDescriptionFor(option.key, String(label))}
                    </small>
                </Col>
                <Col flex='none'>
                    <Button
                        onClick={(event) => {
                            event.stopPropagation();
                            if (missingRequiredPlan || missingRequiredAddOn || dataSourceUsage.reached) {
                                return;
                            }

                            void option.handler();
                        }}
                        disabled={missingRequiredPlan || missingRequiredAddOn || dataSourceUsage.reached}
                        title={dataSourceUsage.reached ? dataSourceUsage.reason : undefined}
                        size='small'
                        type='dashed'
                    >
                        Connect
                    </Button>
                </Col>
            </Row>
        );
    };

    return (
        <>
            <Tabs
                activeKey={visibleTabs.some((tab) => tab.key === activeKey) ? activeKey : COMMAND_PALETTE_ALL_TAB_KEY}
                items={visibleTabs.map((tab) => ({
                    key: tab.key,
                    label: (
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                            }}
                        >
                            {tabIconByKey(tab, 16, false)}
                            <span style={{ marginLeft: '0.25rem' }}>{tab.label}</span>
                            <span style={{ marginLeft: '0.125rem' }}>
                                <SmallTag message={countByTab[tab.key] ?? 0} styles={{ marginLeft: '0.125rem' }} />
                            </span>
                        </span>
                    ),
                }))}
                onChange={onChange}
                size='small'
                tabBarExtraContent={
                    <SmallTooltip title='Customize Command Palette Tabs' placement='left'>
                        <Button onClick={() => setPreferencesOpen(true)} size='small' type='dashed'>
                            <TbSettings2 style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />
                        </Button>
                    </SmallTooltip>
                }
                tabBarStyle={{
                    marginBottom: 0,
                    padding: '0 0.5rem 0 0.5rem',
                }}
            />
            <Modal
                destroyOnClose
                footer={
                    <Row>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='command palette tabs and Enterprise Search' />
                        </Col>
                        <Col flex='auto' />
                    </Row>
                }
                onCancel={() => setPreferencesOpen(false)}
                open={preferencesOpen}
                styles={{ body: preferenceModalBodyStyle }}
                title='Customize Command Palette'
                width={16 * 34}
                zIndex={preferencesModalZIndex}
            >
                {sections.map((section) => {
                    const sectionPreferences = getCommandPaletteSectionPreferences(
                        commandPalettePreferences,
                        section.key,
                    );
                    const orderedItems = orderCommandPaletteTabItems(section.items, sectionPreferences);

                    return (
                        <div key={section.key} style={{ marginBottom: '0.25rem' }}>
                            <Row align='middle' gutter={[8, 0]} style={{ marginBottom: '0.25rem' }} wrap={false}>
                                <Col flex='auto'>
                                    <small style={{ color: token.colorTextTertiary, verticalAlign: '1px' }}>
                                        {section.title}
                                    </small>
                                </Col>
                                <Col flex='none'>
                                    <SmallTooltip title={`Reset ${section.title}`} placement='left' padding='0 0.25rem'>
                                        <Button onClick={() => resetSection(section)} size='small' type='text'>
                                            <TbRefresh style={{ color: token.colorTextTertiary }} />
                                        </Button>
                                    </SmallTooltip>
                                </Col>
                            </Row>
                            <DragDropContext onDragEnd={(result) => handleDragEnd(section, result)}>
                                <Droppable droppableId={`command-palette-${section.key}`}>
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{
                                                backgroundColor: token.colorBgBase,
                                                border: `1px dashed ${token.colorBorder}`,
                                                borderRadius: token.borderRadius,
                                                maxHeight: section.key === 'dataSources' ? '15rem' : undefined,
                                                overflowY: section.key === 'dataSources' ? 'auto' : undefined,
                                                padding: '0.25rem',
                                            }}
                                        >
                                            {orderedItems.map((item, index) => renderManagerItem(section, item, index))}
                                            {provided.placeholder}
                                            {section.key === 'dataSources' &&
                                                unconnectedDataSourceOptions.length !== 0 && (
                                                    <>
                                                        {orderedItems.length !== 0 && (
                                                            <Divider style={{ margin: '0.5rem 0' }} />
                                                        )}
                                                        <div style={{ margin: '0.25rem 0.25rem 0.375rem' }}>
                                                            <small style={{ color: token.colorTextTertiary }}>
                                                                Available Data Sources (
                                                                {unconnectedDataSourceOptions.length})
                                                            </small>
                                                        </div>
                                                        {unconnectedDataSourceOptions.map(renderDataSourceConnectItem)}
                                                    </>
                                                )}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    );
                })}
                <Row gutter={[8, 0]} style={{ marginTop: '0.75rem' }}>
                    <Col span={12}>
                        <Button
                            block
                            onClick={() => {
                                setDataSourceModalView('add');
                                setDataSourceModalOpen(true);
                            }}
                            size='small'
                        >
                            <TbDatabase />
                            Connect Data Source
                        </Button>
                    </Col>
                    <Col span={12}>
                        <Button
                            block
                            onClick={() => {
                                setDataSourceModalView('manage');
                                setDataSourceModalOpen(true);
                            }}
                            size='small'
                        >
                            <TbSettings2 />
                            Manage Sources
                        </Button>
                    </Col>
                </Row>
                <Divider style={{ margin: '0.75rem 0' }} />
                <Button block onClick={resetAll} type='dashed'>
                    <TbRefresh />
                    Reset Command Palette Tabs
                </Button>
            </Modal>
            <AddDataSourceModal
                activeView={dataSourceModalView}
                open={dataSourceModalOpen}
                setOpen={setDataSourceModalOpen}
                zIndex={dataSourceModalZIndex}
            />
        </>
    );
};

export default CommandPaletteTabs;
